import React, { useEffect } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { avgLifecycle, avgMessages, thrAnalyzed } from './icons';
import AnimatematedInfoBar from './elements/infoBar/AnimatedInfoBar';
import Relations from './elements/relations/Relations';
import { GetAnalyticsData } from '../../store/analytics/analytics.actions';
import { User } from '../../types';
import CircleProgressBar from './elements/circleBar/CircleProgressBar';
import Summary from './elements/summary/Summary';
import RelationsSkeleton from './elements/skeletons/RelationsSkeleton';
import {
  createInfoBarsArray,
  createRelationsArray,
} from './elements/data/analyticsData';
import PersonalInfoSkeleton from './elements/skeletons/PersonalInfoSkeleton';
import AnalyzeSkeleton from './elements/skeletons/AnalyzeSkeleton';
import RelationsNoData from './elements/relations/icons/RelationsNoData';

export const Analytics: React.FC = (): JSX.Element => {
  const { user, org } = useSelector((state: RootState) => state.auth);
  const { stats, measurements, isLoading, noData } = useSelector(
    (state: RootState) => state.analytics
  );
  const { orgId, userId } = user as User;

  const dispatch = useDispatch();

  useEffect(() => {
    const fromDate = moment().startOf('month').format('YYYY-MM-DD');
    const toDate = moment().endOf('month').format('YYYY-MM-DD');
    dispatch(GetAnalyticsData(orgId, userId, fromDate, toDate));
  }, []); // eslint-disable-line

  const animationDuration = (measurements?.overallCompetence as number) * 2.5;

  const relationsArray = createRelationsArray(stats, isLoading);

  const infoBarsArray = createInfoBarsArray(
    measurements,
    animationDuration,
    isLoading
  );

  return (
    <div className="personalQ">
      <div className="personalQ__firstColumn">
        <PersonalInfoSkeleton duration={1200} globalLoading={isLoading}>
          <div className="personalQ__firstColumn-profile">
            <span className="personalQ__firstColumn-profile-name">
              {user?.name}
            </span>
            <span className="personalQ__firstColumn-profile-company">
              {org?.avatar ? (
                <img
                  className="personalQ__firstColumn-profile-companyLogo"
                  src={org.avatar}
                  alt="Logo"
                />
              ) : null}{' '}
              {org?.name}
            </span>
            <div className="personalQ__firstColumn-profile-since">
              <span className="personalQ__firstColumn-profile-since-text">
                Member since:
              </span>{' '}
              <span className="personalQ__firstColumn-profile-since-date">
                {moment(user?.createdAt).format('D/M/YY')}
              </span>
            </div>
          </div>
        </PersonalInfoSkeleton>
        <div className="personalQ__firstColumn-analyze">
          <>
            <AnalyzeSkeleton duration={1500} globalLoading={isLoading}>
              <>
                <div className="personalQ__firstColumn-analyze-label">
                  <img src={thrAnalyzed} alt="thred analyzed" /> Active threads
                  analyzed:
                  <span className="personalQ__firstColumn-analyze-label-value">
                    {Math.round(stats?.activeThreadsAnalyzed || 0)}
                  </span>
                </div>
                <div className="personalQ__firstColumn-analyze-label">
                  <img src={avgMessages} alt="messages" />
                  Avg. messages per thread:
                  <span className="personalQ__firstColumn-analyze-label-value">
                    {Math.round(stats?.avgMessagesPerThread || 0)}
                  </span>
                </div>
                <div className="personalQ__firstColumn-analyze-label">
                  <img src={avgLifecycle} alt="life-cycle" />
                  Avg. thread life-cycle (wks):
                  <span className="personalQ__firstColumn-analyze-label-value">
                    {stats?.avgThreadDurationInWeeks?.toFixed(2) || 0}
                  </span>
                </div>
              </>
            </AnalyzeSkeleton>
          </>
          <div className="personalQ__firstColumn-analyze-relations">
            <RelationsSkeleton durationText={1900} globalLoading={isLoading}>
              <>
                <div className="personalQ__firstColumn-analyze-relations__title">
                  Your Relationships
                </div>
                {stats?.mostActiveParticipants &&
                stats?.mostCompetentVs &&
                stats?.leastCompetentVs ? (
                  <>
                    {relationsArray?.map((el: any) => (
                      <Relations
                        key={el.title}
                        title={el.title}
                        people={el.people}
                      />
                    ))}
                  </>
                ) : (
                  <>
                    <RelationsNoData title="MOST ACTIVE" />
                    <RelationsNoData title="MOST COMPETENT WITH" />
                    <RelationsNoData title="LEAST COMPETENT WITH" />
                  </>
                )}
              </>
            </RelationsSkeleton>
          </div>
        </div>
      </div>
      <div className="personalQ__secondColumn">
        <div
          className={
            isLoading
              ? 'loading personalQ__secondColumn-infobars'
              : 'personalQ__secondColumn-infobars'
          }
        >
          {infoBarsArray.map((el) => (
            <AnimatematedInfoBar
              key={el.toolTip}
              title={el.title}
              min={el.min}
              max={el.max}
              current={el.current as number}
              popupMessage={el.toolTip}
              animationTime={el.animationTime}
              skelTitleWidth={el.sketWidth}
              isLoading={el.isLoading}
            />
          ))}
        </div>
        <div className="personalQ__secondColumn-overallcompetence">
          <CircleProgressBar
            measure={measurements?.overallCompetence as number}
            animationTime={animationDuration}
            isLoading={isLoading}
            noData={noData}
            user={user as User}
          />
          <div className="personalQ__secondColumn-overallcompetence-summary">
            <Summary />
          </div>
        </div>
      </div>
    </div>
  );
};
